import React, { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../_redux/education-terms/EducationTermsAction";
import { 
  exportToPDF,
  deletionAlert, 
  exportToExcel,
  getAuthorities, 
  noRecordsAlert,
  redirectToPages, 
  resultAlert,
  ExportButton,
  print,
  copy,
  DeleteButton,
  FormButton
} from "../../../../../_helpers/Helpers";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../_metronic/_partials/controls";
import { EducationTermsFilter } from "./education-terms-filter/EducationTermsFilter";
import { EducationTermsTable } from "./education-terms-table/EducationTermsTable";
import { useEducationTermsUIContext } from "./EducationTermsUIContext";

export function EducationTermsCard() {
  // EducationTerms UI Context
  const educationTermsUIContext = useEducationTermsUIContext();
  const educationTermsUIProps = useMemo(() => {
    return {
      ids: educationTermsUIContext.ids,
      setIds: educationTermsUIContext.setIds,
      queryParams: educationTermsUIContext.queryParams,
      openNewEducationTermPage: educationTermsUIContext.openNewEducationTermPage,
    };
  }, [educationTermsUIContext]);
    
  // Getting curret state of EducationTerm list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.educationTerms }),
    shallowEqual
  );
  const { actionsLoading, isSuccess, type, authorities, semesterCount } = currentState;

  // Authority Parameters
  const { isExportationAuthorized, isAdditionAuthorized, isDeletionAuthorized } = getAuthorities(authorities);

  // EducationTerms Redux state
  const dispatch = useDispatch();

  // Kimlik, Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);

  /** Belli Bir Eğitim Dönemi Listesini Excele Aktarır */
  const exportEducationTermsToExcel = () => {
    dispatch(actions.getAllEducationTerms(educationTermsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      let semesterColumns = [];
      let widths = [20, 30, 30];
      let columns = ["Eğitim Dönemi", "Birinci Dönem Başlangıcı", "Birinci Dönem Sonu"];
      entities.forEach(entity => rows.push([entity.term, entity.firstSemesterStart, entity.firstSemesterEnd]));
  
      if (semesterCount > 1) {
        widths = widths.concat([30, 30]);
        semesterColumns = ["İkinci Dönem Başlangıcı", "İkinci Dönem Sonu"];
        columns = columns.concat(semesterColumns);
        rows.forEach((row, i) => row.push(entities[i].secondSemesterStart, entities[i].secondSemesterEnd));
      }
  
      if (semesterCount > 2) {
        widths = widths.concat([30, 30]);
        semesterColumns = ["Üçüncü Dönem Başlangıcı", "Üçüncü Dönem Sonu"];
        columns = columns.concat(semesterColumns);
        rows.forEach((row, i) => row.push(entities[i].thirdSemesterStart, entities[i].thirdSemesterEnd));
      }
  
      if (semesterCount > 3) {
        widths = widths.concat([30, 30]);
        semesterColumns = ["Dördüncü Dönem Başlangıcı", "Dördüncü Dönem Sonu"];
        columns = columns.concat(semesterColumns);
        rows.forEach((row, i) => row.push(entities[i].fourthSemesterStart, entities[i].fourthSemesterEnd));
      }
  
      widths.push(15);
      columns.push("Aktif mi?");
      rows.forEach((row, i) => row.push(entities[i].isActive ? "Aktif" : "Pasif"));
      const filename = "Eğitim Dönemi Listesi";
      exportToExcel({ columns, rows, widths, filename });
    });    
  }

  /** Belli Bir Eğitim Dönemi Listesini PDFe Aktarır */
  const exportEducationTermsToPDF = () => {
    dispatch(actions.getAllEducationTerms(educationTermsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      let views = [ 
        {
          header: "Eğitim Dönemi",
          rows: entities.map(entity => entity.term)
        },
        {
          header: "Birinci Dönem Başlangıcı",
          rows: entities.map(entity => entity.firstSemesterStart)
        },
        {
          header: "Birinci Dönem Sonu",
          rows: entities.map(entity => entity.firstSemesterEnd)
        }
      ];
  
      if (semesterCount > 1) {
        views = views.concat([
          {
            header: "İkinci Dönem Başlangıcı",
            rows: entities.map(entity => entity.secondSemesterStart)
          },
          {
            header: "İkinci Dönem Sonu",
            rows: entities.map(entity => entity.secondSemesterEnd)
          }
        ]);
      }
  
      if (semesterCount > 2) {
        views = views.concat([
          {
            header: "Üçüncü Dönem Başlangıcı",
            rows: entities.map(entity => entity.thirdSemesterStart)
          },
          {
            header: "Üçüncü Dönem Sonu",
            rows: entities.map(entity => entity.thirdSemesterEnd)
          }
        ]);
      }
  
      if (semesterCount > 3) {
        views = views.concat([
          {
            header: "Dördüncü Dönem Başlangıcı",
            rows: entities.map(entity => entity.fourthSemesterStart)
          },
          {
            header: "Dördüncü Dönem Sonu",
            rows: entities.map(entity => entity.fourthSemesterEnd)
          }
        ]);
      }
  
      views.push({
        header: "Aktif mi?",
        rows: entities.map(entity => entity.isActive ? "Aktif" : "Pasif")
      });
  
      const filename = "Eğitim Dönemi Listesi";
      exportToPDF(views, filename);
    });    
  }

  /** Belli Bir Eğitim Dönemi Listesini Yazdırma Penceresini Açar */
  const printEducationTerms = () => {
    dispatch(actions.getAllEducationTerms(educationTermsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];
      let semesterProperties;
      const header = "Eğitim Dönemi Listesi";
      let properties = ["Eğitim Dönemi", "Birinci Dönem Başlangıcı", "Birinci Dönem Sonu"];
  
      entities.forEach(entity => {
        data.push({
          "Eğitim Dönemi": entity.term,
          "Birinci Dönem Başlangıcı": entity.firstSemesterStart,
          "Birinci Dönem Sonu": entity.firstSemesterEnd
        });
      });
  
      if (semesterCount > 1) {
        semesterProperties = ["İkinci Dönem Başlangıcı", "İkinci Dönem Sonu"];
        properties = properties.concat(semesterProperties);
        data.forEach((datium, i) => {
          datium["İkinci Dönem Başlangıcı"] = entities[i].secondSemesterStart;
          datium["İkinci Dönem Sonu"] = entities[i].secondSemesterEnd;
        });
      }
  
      if (semesterCount > 2) {
        semesterProperties = ["Üçüncü Dönem Başlangıcı", "Üçüncü Dönem Sonu"];
        properties = properties.concat(semesterProperties);
        data.forEach((datium, i) => {
          datium["Üçüncü Dönem Başlangıcı"] = entities[i].thirdSemesterStart;
          datium["Üçüncü Dönem Sonu"] = entities[i].thirdSemesterEnd;
        });
      }
  
      if (semesterCount > 3) {
        semesterProperties = ["Dördüncü Dönem Başlangıcı", "Dördüncü Dönem Sonu"];
        properties = properties.concat(semesterProperties);
        data.forEach((datium, i) => {
          datium["Dördüncü Dönem Başlangıcı"] = entities[i].fourthSemesterStart;
          datium["Dördüncü Dönem Sonu"] = entities[i].fourthSemesterEnd;
        });
      }
  
      properties.push("Aktif mi?");    
      data.forEach((datium, i) => datium["Aktif mi?"] = entities[i].isActive ? "Aktif" : "Pasif");
      print({ data, properties, header });
    });
  }

  /** Belli Bir Eğitim Dönemi Listesini Panoya Kopyalar */
  const copyEducationTerms = () => {   
    dispatch(actions.getAllEducationTerms(educationTermsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      let semesterColumns = [];
      const header = "Eğitim Dönemi Listesi";
      let columns = ["Eğitim Dönemi", "Birinci Dönem Başlangıcı", "Birinci Dönem Sonu"];
      entities.forEach(entity => rows.push([entity.term, entity.firstSemesterStart, entity.firstSemesterEnd]));

      if (semesterCount > 1) {
        semesterColumns = ["İkinci Dönem Başlangıcı", "İkinci Dönem Sonu"];
        columns = columns.concat(semesterColumns);
        rows.forEach((row, i) => row.push(entities[i].secondSemesterStart, entities[i].secondSemesterEnd));
      }

      if (semesterCount > 2) {
        semesterColumns = ["Üçüncü Dönem Başlangıcı", "Üçüncü Dönem Sonu"];
        columns = columns.concat(semesterColumns);
        rows.forEach((row, i) => row.push(entities[i].thirdSemesterStart, entities[i].thirdSemesterEnd));
      }
  
      if (semesterCount > 3) {
        semesterColumns = ["Dördüncü Dönem Başlangıcı", "Dördüncü Dönem Sonu"];
        columns = columns.concat(semesterColumns);
        rows.forEach((row, i) => row.push(entities[i].fourthSemesterStart, entities[i].fourthSemesterEnd));
      }
  
      columns.push("Aktif mi?");
      rows.forEach((row, i) => row.push(entities[i].isActive ? "Aktif" : "Pasif"));
      copy({ header, columns, rows });
    });    
  }

  /** Belli Bir Eğitim Dönemi Listesini Siler */
  const deleteEducationTerms = (ids) => {
    if (typeof ids === 'string') ids = ids.split(",");

    if (ids.length < 1) {
      noRecordsAlert();
      return;
    }

    deletionAlert().then(result => {
      if (result.isConfirmed) dispatch(actions.deleteEducationTerms(ids)).then(result => onResult(result));
    });
  }

  const onResult = ({ isSuccess, type, message }) => {
    resultAlert(type, message);
  
    if (isSuccess) {
      setTimeout(() => {
        educationTermsUIProps.setIds([]);
        dispatch(actions.getEducationTerms(educationTermsUIProps.queryParams));
      }, 2500);
    }
  };
  
  const title = <>Eğitim Dönemi Listesi{educationTermsUIProps.ids.length > 0 ?
                  <> [Seçilen Kayıt Sayısı: <span style={{color:"red"}}>{educationTermsUIProps.ids.length}</span>]</> :
                   ""}
                </>;

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title={title}>
        {
          (isExportationAuthorized || isAdditionAuthorized || isDeletionAuthorized) &&
          <CardHeaderToolbar>
            {
              isExportationAuthorized &&
              <ExportButton
                exportToExcel={exportEducationTermsToExcel}
                exportToPDF={exportEducationTermsToPDF}
                print={printEducationTerms}
                copy={copyEducationTerms}
              />
            }
            {
              isAdditionAuthorized && 
              <FormButton 
                path="Navigation/Plus"
                title="Yeni Eğitim Dönemi"
                method={educationTermsUIProps.openNewEducationTermPage}
              />
            }              
            {
              isDeletionAuthorized && 
              <DeleteButton
                title="Seçilenleri Sil"
                method={() => deleteEducationTerms(educationTermsUIProps.ids)}
              />
            }
          </CardHeaderToolbar>
        }        
      </CardHeader>
      <CardBody>
        <EducationTermsFilter />
        <EducationTermsTable
          deleteEducationTerms={deleteEducationTerms}
        />
      </CardBody>
    </Card>
  );
}